import { useAuth0 } from '@auth0/auth0-react';
import { ChevronDown, ChevronRight, PanelsTopLeft } from 'lucide-react';
import { Fragment, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { describeJudgment } from '../issue-judgment';

import { IssueMetadataPanel } from './IssueMetadataPanel';

import type { OnlineEvalIssue } from '../online-eval.types';

const formatDate = (value: string) => new Date(value).toLocaleString();

export const IssuesTable = ({
  issues,
  onViewDebugSkeleton,
}: {
  issues: OnlineEvalIssue[];
  onViewDebugSkeleton: (issueId: string) => void;
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { user } = useAuth0();

  if (issues.length === 0) {
    return (
      <div className="rounded-lg border py-16 text-center text-sm text-muted-foreground">
        No findings match these filters.
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Category</TableHead>
            <TableHead className="min-w-72">Evaluator judgment</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Agent / model</TableHead>
            <TableHead>Message time</TableHead>
            <TableHead>Found</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {issues.map((issue) => {
            const expanded = expandedId === issue.id;
            return (
              <Fragment key={issue.id}>
                <TableRow>
                  <TableCell className="pr-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setExpandedId(expanded ? null : issue.id)}
                      aria-label={
                        expanded ? 'Collapse metadata' : 'Expand metadata'
                      }
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{issue.category_name}</Badge>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <p className="line-clamp-3">
                      {describeJudgment(issue.judgment)}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-52 truncate">{issue.user_email}</div>
                    <div className="text-xs text-muted-foreground">
                      {issue.user_id}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>{issue.agent || '—'}</div>
                    <div className="max-w-48 truncate text-xs text-muted-foreground">
                      {issue.model || '—'}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {formatDate(issue.message_created_at)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {formatDate(issue.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewDebugSkeleton(issue.id)}
                    >
                      <PanelsTopLeft className="h-4 w-4" />
                      {user?.email === issue.user_email
                        ? 'View chat'
                        : 'View skeleton'}
                    </Button>
                  </TableCell>
                </TableRow>
                {expanded && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={9} className="p-0">
                      <IssueMetadataPanel issue={issue} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
